import types from '../../mutation-types';
import getters from './getters';
import actions from './actions';
import {
  TryVueSet,
  findPendingMessageIndex,
  MAX_SUBSCRIBED_CONVERSATIONS,
  mergeConversationPreservingMessages,
  stampMergedConversationsInList,
  freezeRow,
  cloneConversationForCurrent,
  rebuildConversationIndex,
  getRowIndex,
  replaceConversationInList,
  stripProvisionalFetchState,
} from './helpers';
import { MESSAGE_STATUS } from 'shared/constants/messages';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from '../../../../shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

// Maximum number of recent conversations that retain full message history.
const MAX_RECENT_FULL_CONVERSATIONS = 10;

// Maximum messages kept for conversations outside the recent-full LRU.
// Prevents unbounded memory growth from WebSocket messages.
const MAX_DEGRADED_MESSAGES = 20;

const hydrateAudioTranscriptions = (_state, messages) => {
  // Build a single merged object and assign state once, instead of spreading and
  // reassigning per message. This runs in hot paths (UPDATE_CONVERSATION,
  // SET_PREVIOUS_CONVERSATIONS) that iterate the full message list, where the
  // per-iteration reassignment is the Vue 2 reactivity-churn cost AGENTS.md warns about.
  let mergedTranscriptions = null;
  let mergedDisplay = null;
  messages.forEach(message => {
    const transcriptions = message?.content_attributes?.transcriptions;
    if (!transcriptions) return;
    mergedTranscriptions = mergedTranscriptions || {
      ..._state.audioTranscriptions,
    };
    mergedDisplay = mergedDisplay || { ..._state.audioTranscriptionDisplay };
    Object.keys(transcriptions).forEach(attachmentId => {
      if (attachmentId in mergedTranscriptions) return;
      mergedTranscriptions[attachmentId] = transcriptions[attachmentId];
      mergedDisplay[attachmentId] = true;
    });
  });
  if (mergedTranscriptions) {
    _state.audioTranscriptions = mergedTranscriptions;
    _state.audioTranscriptionDisplay = mergedDisplay;
  }
};

const degradeConversation = conversation => {
  if (!conversation) return;
  const msgs = conversation.messages || [];
  if (msgs.length > 1) {
    const last = msgs[msgs.length - 1];
    conversation.messages = [last];
  }
  if (conversation.allMessagesLoaded) conversation.allMessagesLoaded = false;
  if (conversation.dataFetched) conversation.dataFetched = undefined;
};

const state = {
  allConversations: [],
  // id -> index into allConversations, kept in sync by the mutations below.
  // Turns the per-event O(n) row lookups (filter/find/findIndex) into O(1) so
  // WebSocket bursts over large lists don't scan thousands of rows per event.
  conversationIndexById: new Map(),
  attachments: {},
  audioTranscriptions: {},
  audioTranscriptionDisplay: {},
  listLoadingStatus: true,
  chatStatusFilter: wootConstants.STATUS_TYPE.OPEN,
  chatSortFilter: wootConstants.SORT_BY_TYPE.LATEST,
  currentInbox: null,
  recentFullConversationIds: [],
  appliedFilters: [],
  contextMenuChatId: null,
  conversationParticipants: [],
  conversationLastSeen: null,
  syncConversationsMessages: {},
  conversationFilters: {},
  subscribedNonAssignedConversations: [],
  currentConversation: null,
  currentConversationId: null,
};

// mutations
// Delta payloads (e.g. conversation.read) omit these keys entirely — only
// apply what the event actually carries (null is a real value: unassign /
// priority cleared).
const applyCurrentConversationMetadata = (current, conversation) => {
  const { meta } = conversation;
  if (meta && 'assignee' in meta) {
    TryVueSet(current.meta, 'assignee', meta.assignee);
  }
  if (meta && 'team' in meta) {
    TryVueSet(current.meta, 'team', meta.team);
  }
  if ('priority' in conversation) {
    TryVueSet(current, 'priority', conversation.priority);
  }
  if ('sla' in conversation) {
    TryVueSet(current, 'sla', conversation.sla);
  }
};

export const mutations = {
  [types.SET_ALL_CONVERSATION](_state, conversationList) {
    const newAllConversations = [..._state.allConversations];
    conversationList.forEach(conversation => {
      const indexInCurrentList = newAllConversations.findIndex(
        c => c.id === conversation.id
      );
      if (indexInCurrentList < 0) {
        // After emptyAllConversations, the current conversation is re-added
        // as "new". Preserve its full message history from currentConversation
        // so a subsequent UPDATE_CONVERSATION doesn't lose them.
        if (
          conversation.id === _state.currentConversationId &&
          _state.currentConversation
        ) {
          const merged = mergeConversationPreservingMessages(
            _state.currentConversation,
            conversation
          );
          newAllConversations.push(
            freezeRow(stripProvisionalFetchState(merged))
          );
          _state.currentConversation = cloneConversationForCurrent(merged);
        } else {
          if (!_state.recentFullConversationIds.includes(conversation.id)) {
            degradeConversation(conversation);
          }
          newAllConversations.push(freezeRow(conversation));
        }
      } else {
        const existing = newAllConversations[indexInCurrentList];
        const merged = mergeConversationPreservingMessages(
          existing,
          conversation
        );
        newAllConversations[indexInCurrentList] = freezeRow(merged);
        if (conversation.id === _state.currentConversationId) {
          // Rebuild the reactive clone from the live currentConversation, not the
          // frozen row, so dataFetched/messages/allMessagesLoaded survive the update.
          _state.currentConversation = cloneConversationForCurrent(
            mergeConversationPreservingMessages(
              _state.currentConversation,
              conversation
            )
          );
        }
      }
    });
    _state.allConversations = newAllConversations;
    rebuildConversationIndex(_state);
  },

  [types.REPLACE_ALL_CONVERSATIONS](_state, conversationList) {
    const newAllConversations = [];
    conversationList.forEach(conversation => {
      if (
        conversation.id === _state.currentConversationId &&
        _state.currentConversation
      ) {
        const merged = mergeConversationPreservingMessages(
          _state.currentConversation,
          conversation
        );
        newAllConversations.push(freezeRow(stripProvisionalFetchState(merged)));
        _state.currentConversation = cloneConversationForCurrent(merged);
      } else {
        if (!_state.recentFullConversationIds.includes(conversation.id)) {
          degradeConversation(conversation);
        }
        newAllConversations.push(freezeRow(conversation));
      }
    });

    if (
      _state.currentConversationId &&
      !newAllConversations.some(c => c.id === _state.currentConversationId) &&
      _state.currentConversation
    ) {
      // Keep the reactive currentConversation as-is; the list gets its own frozen
      // copy so the open thread is never a frozen row.
      newAllConversations.push(
        freezeRow(
          stripProvisionalFetchState(
            cloneConversationForCurrent(_state.currentConversation)
          )
        )
      );
    }
    _state.allConversations = newAllConversations;
    rebuildConversationIndex(_state);
  },

  [types.EMPTY_ALL_CONVERSATION](_state) {
    _state.allConversations = [];
    rebuildConversationIndex(_state);
  },

  [types.SET_ALL_MESSAGES_LOADED](_state, id = _state.currentConversationId) {
    replaceConversationInList(_state, id, draft => {
      draft.allMessagesLoaded = true;
    });

    // Only touch the reactive clone when the flag belongs to the open chat — a
    // fetch that resolved after the user switched conversations must not mark the
    // now-current one as fully loaded.
    if (_state.currentConversationId === id && _state.currentConversation) {
      TryVueSet(_state.currentConversation, 'allMessagesLoaded', true);
    }
  },

  // Stamps "the thread history was already fetched" on BOTH copies of the
  // conversation, which is what keeps it from being refetched on every reopen:
  //   - the list row, because SET_CURRENT_CHAT_WINDOW clones the row into
  //     currentConversation, so a row carrying the flag short-circuits the fetch
  //     the next time the conversation is opened from the list
  //     (ChatList.refreshConversations / InboxView both pass a row);
  //   - the reactive clone, which is what MessagesView reads (spinner while
  //     `dataFetched === undefined`, scroll-up pagination gated on `=== true`).
  // Safe against message eviction: degradeConversation clears the flag whenever
  // it truncates a row's thread, and mergeConversationPreservingMessages drops
  // `dataFetched` from incoming payloads, so it is never resurrected by an update.
  [types.SET_CONVERSATION_DATA_FETCHED](_state, id) {
    replaceConversationInList(_state, id, draft => {
      draft.dataFetched = true;
    });

    // Only touch the reactive clone while this is still the open chat — a fetch
    // that resolved after the user switched conversations must not mark the
    // now-current one as fetched.
    if (_state.currentConversationId === id && _state.currentConversation) {
      TryVueSet(_state.currentConversation, 'dataFetched', true);
      TryVueSet(_state.currentConversation, 'dataFetchFailed', false);
    }
  },

  [types.SET_CONVERSATION_DATA_FETCH_FAILED](_state, id) {
    if (_state.currentConversationId === id && _state.currentConversation) {
      TryVueSet(_state.currentConversation, 'dataFetched', true);
      TryVueSet(_state.currentConversation, 'dataFetchFailed', true);
    }
  },

  [types.CLEAR_ALL_MESSAGES_LOADED](_state) {
    replaceConversationInList(_state, _state.currentConversationId, draft => {
      draft.allMessagesLoaded = false;
    });

    const chatCurrent = _state.currentConversation;
    if (chatCurrent) TryVueSet(chatCurrent, 'allMessagesLoaded', false);
  },

  [types.CLEAR_CURRENT_CHAT_WINDOW](_state) {
    _state.currentConversationId = null;
    _state.currentConversation = null;
  },

  [types.SET_PREVIOUS_CONVERSATIONS](_state, { id, data }) {
    if (data.length) {
      hydrateAudioTranscriptions(_state, data);
      const listIndex = getRowIndex(_state, id);
      if (listIndex !== -1) {
        const chatOnList = _state.allConversations[listIndex];
        const uniqueMessages = data.filter(
          message =>
            !chatOnList.messages.some(
              existingMessage => existingMessage.id === message.id
            )
        );
        if (uniqueMessages.length) {
          replaceConversationInList(_state, id, draft => {
            draft.messages = [...uniqueMessages, ...chatOnList.messages];
          });
        }
      }

      // remove from current conversation
      const chatCurrent =
        _state.currentConversationId === id
          ? _state.currentConversation
          : undefined;

      if (chatCurrent) {
        const uniqueMessages = data.filter(
          message =>
            !chatCurrent.messages.some(
              existingMessage => existingMessage.id === message.id
            )
        );
        chatCurrent.messages.unshift(...uniqueMessages);
      }
    }
  },

  [types.SET_ALL_ATTACHMENTS](_state, { id, data }) {
    const attachments = _state.attachments[id] || [];

    attachments.push(...data);
    _state.attachments[id] = [...attachments];
  },

  [types.SET_MISSING_MESSAGES](_state, { id, data }) {
    hydrateAudioTranscriptions(_state, data);
    // Give each side its own array. Handing `data` to both would alias the
    // frozen row's thread to the reactive one, and the open thread is mutated in
    // place (ADD_MESSAGE pushes, SET_PREVIOUS_CONVERSATIONS unshifts) — those
    // writes would reach into the row without going through the replace-slot,
    // changing it with no dependency notification.
    replaceConversationInList(_state, id, draft => {
      draft.messages = [...data];
    });

    const chatCurrent =
      _state.currentConversationId === id
        ? _state.currentConversation
        : undefined;
    if (chatCurrent) TryVueSet(chatCurrent, 'messages', [...data]);
  },

  [types.SET_CURRENT_CHAT_WINDOW](_state, conversation) {
    if (conversation) {
      // verify if conversation is already in the list
      const ids = _state.recentFullConversationIds.filter(
        id => id !== conversation.id
      );
      ids.push(conversation.id);

      // Degrade conversations that fall outside the limit. The evicted row is
      // frozen, so degrade it via replace-slot — an in-place mutation would be a
      // silent no-op.
      while (ids.length > MAX_RECENT_FULL_CONVERSATIONS) {
        const removedId = ids.shift();
        replaceConversationInList(_state, removedId, degradeConversation);
      }

      // set top 10 conversation with messages to optimizes performance
      _state.recentFullConversationIds = ids;

      // set current conversation. currentConversation is the reactive thread copy
      // (deep reactivity for messages) — never the frozen list row.
      _state.currentConversationId = conversation.id;
      _state.currentConversation = cloneConversationForCurrent(conversation);
      hydrateAudioTranscriptions(_state, conversation.messages || []);
    }
  },

  [types.ASSIGN_AGENT](_state, assignee) {
    replaceConversationInList(_state, _state.currentConversationId, draft => {
      draft.meta = { ...draft.meta, assignee };
    });

    const chatCurrent = _state.currentConversation;
    if (chatCurrent) TryVueSet(chatCurrent.meta, 'assignee', assignee);
  },

  [types.ASSIGN_TEAM](_state, { team, conversationId }) {
    // chat into list
    replaceConversationInList(_state, conversationId, draft => {
      draft.meta = { ...draft.meta, team };
    });

    // current chat
    const chatCurrent =
      _state.currentConversationId === conversationId
        ? _state.currentConversation
        : undefined;
    if (chatCurrent) TryVueSet(chatCurrent.meta, 'team', team);
  },

  [types.UPDATE_CONVERSATION_LAST_ACTIVITY](
    _state,
    { lastActivityAt, conversationId }
  ) {
    // chat on list
    replaceConversationInList(_state, conversationId, draft => {
      draft.last_activity_at = lastActivityAt;
    });

    // current chat
    const chatCurrent =
      _state.currentConversationId === conversationId
        ? _state.currentConversation
        : undefined;
    if (chatCurrent) TryVueSet(chatCurrent, 'last_activity_at', lastActivityAt);
  },

  [types.ASSIGN_PRIORITY](_state, { priority, conversationId }) {
    replaceConversationInList(_state, conversationId, draft => {
      draft.priority = priority;
    });

    const chatCurrent =
      _state.currentConversationId === conversationId
        ? _state.currentConversation
        : undefined;
    if (chatCurrent) TryVueSet(chatCurrent, 'priority', priority);
  },

  [types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES](_state, custom_attributes) {
    replaceConversationInList(_state, _state.currentConversationId, draft => {
      draft.custom_attributes = custom_attributes;
    });

    const chatCurrent = _state.currentConversation;
    if (chatCurrent)
      TryVueSet(chatCurrent, 'custom_attributes', custom_attributes);
  },

  [types.CHANGE_CONVERSATION_STATUS](
    _state,
    { conversationId, status, snoozedUntil }
  ) {
    replaceConversationInList(_state, Number(conversationId), draft => {
      draft.snoozed_until = snoozedUntil;
      draft.status = status;
    });

    // Only update currentConversation if the conversationId matches the current conversation
    if (
      _state.currentConversationId === Number(conversationId) &&
      _state.currentConversation
    ) {
      TryVueSet(_state.currentConversation, 'snoozed_until', snoozedUntil);
      TryVueSet(_state.currentConversation, 'status', status);
    }
  },

  [types.MUTE_CONVERSATION](_state) {
    replaceConversationInList(
      _state,
      Number(_state.currentConversationId),
      draft => {
        draft.muted = true;
      }
    );

    const chatCurrent = _state.currentConversation;
    if (chatCurrent) TryVueSet(chatCurrent, 'muted', true);
  },

  [types.UNMUTE_CONVERSATION](_state) {
    replaceConversationInList(
      _state,
      Number(_state.currentConversationId),
      draft => {
        draft.muted = false;
      }
    );

    const chatCurrent = _state.currentConversation;
    if (chatCurrent) TryVueSet(chatCurrent, 'muted', false);
  },

  [types.MERGE_CONVERSATIONS](_state, data) {
    // Stamp losers in place (merged_into_id + resolved) instead of dropping them, so
    // they keep the "Merged" badge and fall out of active filters without a refetch.
    // ids are display_ids. Cross-agent live sync is CONVERSATION_MERGED_REALTIME below.
    const mergedConversationIds = data?.merged_conversation_ids || [];
    if (!mergedConversationIds.length) return;

    // Only stamp when the survivor id is present: stamping merged_into_id with a
    // falsy value would flag the loser as "not merged", silently leaving its
    // composer live. Clearing the current chat below is independent of it.
    const survivorId = data?.target?.id;
    if (survivorId != null) {
      stampMergedConversationsInList(_state, mergedConversationIds, survivorId);
    }

    // The initiator has a merged-away source open: clear the selection (they just
    // confirmed the merge and are routed to the survivor). The non-initiator path
    // below deliberately does NOT blank the view — see there.
    if (
      _state.currentConversationId &&
      mergedConversationIds.includes(Number(_state.currentConversationId))
    ) {
      _state.currentConversationId = null;
      _state.currentConversation = null;
    }
  },

  // F9 — real-time cross-agent sync from the `conversation.merged` AC event
  // (helper/actionCable.js#onConversationMerged). Unlike MERGE_CONVERSATIONS (initiator),
  // a non-initiator's open loser is NOT blanked — we stamp merged_into_id so F6's banner
  // and disabled composer render live instead of the row vanishing under them.
  [types.CONVERSATION_MERGED_REALTIME](_state, data) {
    const sourceIds = data?.sourceIds || [];
    const { survivorId } = data || {};
    // Same guard as MERGE_CONVERSATIONS: a null survivorId would stamp a falsy
    // merged_into_id and fail to lock the loser into read-only.
    if (!sourceIds.length || survivorId == null) return;

    // Stamp merged_into_id + resolved on matching list entries so they render
    // with the "Merged" badge (and drop out of active filters) in place — never
    // removed from the list, so a viewing agent doesn't see rows vanish.
    stampMergedConversationsInList(_state, sourceIds, survivorId);

    if (
      _state.currentConversationId &&
      sourceIds.includes(Number(_state.currentConversationId)) &&
      _state.currentConversation
    ) {
      // Stamp status too (not just merged_into_id): the open loser is now resolved,
      // so status-driven UI (e.g. the Resolve button) reads correctly instead of a
      // stale 'open' until the next refetch. Mirrors the list-entry stamp above.
      TryVueSet(_state.currentConversation, 'merged_into_id', survivorId);
      TryVueSet(_state.currentConversation, 'status', 'resolved');
    }
  },

  [types.TOGGLE_EMAIL_CONTINUITY](_state, { data = {}, conversationId } = {}) {
    const { inbox_id: inboxId } = data;

    replaceConversationInList(_state, Number(conversationId), draft => {
      draft.additional_attributes = {
        ...draft.additional_attributes,
        force_email_channel: true,
      };
      if (inboxId) {
        draft.inbox_id = inboxId;
      }
    });

    // Only update currentConversation if the conversationId matches the current conversation
    if (
      _state.currentConversationId === Number(conversationId) &&
      _state.currentConversation
    ) {
      TryVueSet(_state.currentConversation, 'additional_attributes', {
        ..._state.currentConversation.additional_attributes,
        force_email_channel: true,
      });
      if (inboxId) {
        TryVueSet(_state.currentConversation, 'inbox_id', inboxId);
      }
    }
  },

  [types.ADD_CONVERSATION_ATTACHMENTS](_state, message) {
    // early return if the message has not been sent, or has no attachments
    if (
      message.status !== MESSAGE_STATUS.SENT ||
      !message.attachments?.length
    ) {
      return;
    }

    // Skip storing attachments for conversations outside recent full set.
    if (!_state.recentFullConversationIds.includes(message.conversation_id)) {
      return;
    }

    const id = message.conversation_id;
    const existingAttachments = _state.attachments[id] || [];

    const attachmentsToAdd = message.attachments.filter(attachment => {
      // if the attachment is not already in the store, add it
      // this is to prevent duplicates
      return !existingAttachments.some(
        existingAttachment => existingAttachment.id === attachment.id
      );
    });

    // replace the attachments in the store
    _state.attachments[id] = [...existingAttachments, ...attachmentsToAdd];
  },

  [types.DELETE_CONVERSATION_ATTACHMENTS](_state, message) {
    if (message.status !== MESSAGE_STATUS.SENT) return;

    const { conversation_id: id } = message;
    const existingAttachments = _state.attachments[id] || [];
    if (!existingAttachments.length) return;

    _state.attachments[id] = existingAttachments.filter(attachment => {
      return attachment.message_id !== message.id;
    });
  },

  [types.SET_AUDIO_TRANSCRIPTION](_state, { attachmentId, transcription }) {
    _state.audioTranscriptions = {
      ..._state.audioTranscriptions,
      [attachmentId]: transcription,
    };
    _state.audioTranscriptionDisplay = {
      ..._state.audioTranscriptionDisplay,
      [attachmentId]: true,
    };
  },

  [types.TOGGLE_AUDIO_TRANSCRIPTION_DISPLAY](_state, { attachmentId }) {
    _state.audioTranscriptionDisplay = {
      ..._state.audioTranscriptionDisplay,
      [attachmentId]: !_state.audioTranscriptionDisplay[attachmentId],
    };
  },

  [types.ADD_MESSAGE](_state, message) {
    const conversationId = Number(message.conversation_id);
    hydrateAudioTranscriptions(_state, [message]);

    const listIndex = getRowIndex(_state, conversationId);

    const chatCurrent = _state.currentConversation;

    if (listIndex !== -1) {
      const chatOnList = _state.allConversations[listIndex];
      const pendingMessageIndexOnList = findPendingMessageIndex(
        chatOnList,
        message
      );

      if (pendingMessageIndexOnList !== -1) {
        const messages = [...chatOnList.messages];
        messages[pendingMessageIndexOnList] = message;
        replaceConversationInList(_state, conversationId, draft => {
          draft.messages = messages;
        });
      } else {
        const messageExists = chatOnList.messages.some(
          m => m.id === message.id
        );

        if (!messageExists) {
          const isFull =
            _state.recentFullConversationIds.includes(conversationId);
          let messages = [...chatOnList.messages, message];
          // Cap messages for non-full conversations to bound memory.
          if (!isFull && messages.length > MAX_DEGRADED_MESSAGES) {
            messages = messages.slice(-MAX_DEGRADED_MESSAGES);
          }
          const { conversation: { unread_count: unreadCount = 0 } = {} } =
            message;
          replaceConversationInList(_state, conversationId, draft => {
            draft.messages = messages;
            draft.timestamp = message.created_at;
            draft.unread_count = unreadCount;
          });
          if (_state.currentConversationId === conversationId) {
            emitter.emit(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS);
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
          }
        }
      }
    }

    // Only update the current conversation thread if the incoming message
    // belongs to the currently active conversation. Otherwise, ignore to
    // prevent cross-tab leakage.
    if (chatCurrent && _state.currentConversationId === conversationId) {
      const pendingMessageIndexCurrent = findPendingMessageIndex(
        chatCurrent,
        message
      );

      if (pendingMessageIndexCurrent !== -1) {
        TryVueSet(chatCurrent.messages, pendingMessageIndexCurrent, message);
      } else {
        const messageExists = chatCurrent.messages.some(
          m => m.id === message.id
        );

        if (!messageExists) {
          chatCurrent.messages.push(message);
          chatCurrent.timestamp = message.created_at;
          const { conversation: { unread_count: unreadCount = 0 } = {} } =
            message;
          chatCurrent.unread_count = unreadCount;
          emitter.emit(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS);
          emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        }
      }
    }
  },

  [types.ADD_CONVERSATION](_state, conversation) {
    const frozen = freezeRow(conversation);
    _state.allConversations.push(frozen);
    if (_state.conversationIndexById) {
      _state.conversationIndexById.set(
        frozen.id,
        _state.allConversations.length - 1
      );
    }
  },

  [types.UPDATE_CURRENT_CONVERSATION_ONLY](_state, conversation) {
    if (
      _state.currentConversationId !== conversation.id ||
      !_state.currentConversation
    ) {
      return;
    }
    _state.currentConversation = cloneConversationForCurrent(
      mergeConversationPreservingMessages(
        _state.currentConversation,
        conversation
      )
    );
    applyCurrentConversationMetadata(_state.currentConversation, conversation);
  },

  [types.UPDATE_CONVERSATION](_state, conversation) {
    hydrateAudioTranscriptions(_state, conversation.messages || []);
    const currentConversationIndex = getRowIndex(_state, conversation.id);

    if (currentConversationIndex > -1) {
      const existing = _state.allConversations[currentConversationIndex];
      const merged = mergeConversationPreservingMessages(
        existing,
        conversation
      );
      // Same slot, so the id -> index map stays valid without a rebuild.
      TryVueSet(
        _state.allConversations,
        currentConversationIndex,
        freezeRow(merged)
      );
      if (_state.currentConversationId === conversation.id) {
        // Rebuild the reactive clone from the live currentConversation, not the
        // frozen row, so dataFetched/messages/allMessagesLoaded survive the update.
        _state.currentConversation = cloneConversationForCurrent(
          mergeConversationPreservingMessages(
            _state.currentConversation,
            conversation
          )
        );
        emitter.emit(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS);
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    } else {
      if (!_state.recentFullConversationIds.includes(conversation.id)) {
        degradeConversation(conversation);
      }

      const frozen = freezeRow(conversation);
      _state.allConversations.push(frozen);
      if (_state.conversationIndexById) {
        _state.conversationIndexById.set(
          frozen.id,
          _state.allConversations.length - 1
        );
      }
    }

    // Only update currentConversation metadata if the updated conversation is the current one
    if (
      _state.currentConversationId === conversation.id &&
      _state.currentConversation
    ) {
      applyCurrentConversationMetadata(
        _state.currentConversation,
        conversation
      );
    }
  },

  // Prunes a conversation the server has hard-deleted (see getConversation's 404
  // handler in actions.js) — nothing broadcasts the delete, so this 404 is the only
  // signal the client gets.
  [types.REMOVE_CONVERSATION](_state, conversationId) {
    // Route params arrive as strings; row/currentConversationId ids are numeric.
    const id = Number(conversationId);
    _state.allConversations = _state.allConversations.filter(c => c.id !== id);
    rebuildConversationIndex(_state);

    if (_state.currentConversationId === id) {
      _state.currentConversationId = null;
      _state.currentConversation = null;
    }
  },

  [types.SET_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = true;
  },

  [types.CLEAR_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = false;
  },

  [types.UPDATE_MESSAGE_UNREAD_COUNT](
    _state,
    { id, lastSeen, unreadCount = 0 }
  ) {
    replaceConversationInList(_state, id, draft => {
      draft.agent_last_seen_at = lastSeen;
      draft.unread_count = unreadCount;
    });

    const chatCurrent =
      _state.currentConversationId === id
        ? _state.currentConversation
        : undefined;

    if (chatCurrent) {
      TryVueSet(chatCurrent, 'agent_last_seen_at', lastSeen);
      TryVueSet(chatCurrent, 'unread_count', unreadCount);
    }
  },

  [types.CHANGE_CHAT_STATUS_FILTER](_state, data) {
    _state.chatStatusFilter = data;
  },

  [types.CHANGE_CHAT_SORT_FILTER](_state, data) {
    _state.chatSortFilter = data;
  },

  [types.UPDATE_ASSIGNEE](_state, payload) {
    replaceConversationInList(_state, payload.id, draft => {
      draft.meta = { ...draft.meta, assignee: payload.assignee };
    });

    // Only update currentConversation if the payload is for the current conversation
    if (
      _state.currentConversationId === payload.id &&
      _state.currentConversation
    ) {
      TryVueSet(_state.currentConversation.meta, 'assignee', payload.assignee);
    }
  },

  [types.UPDATE_CONVERSATION_CONTACT](_state, { conversationId, ...payload }) {
    replaceConversationInList(_state, conversationId, draft => {
      draft.meta = { ...draft.meta, sender: payload };
    });

    const chatCurrent =
      _state.currentConversationId === conversationId
        ? _state.currentConversation
        : undefined;

    if (chatCurrent) TryVueSet(chatCurrent.meta, 'sender', payload);
  },

  [types.SET_ACTIVE_INBOX](_state, inboxId) {
    _state.currentInbox = inboxId ? parseInt(inboxId, 10) : null;
  },

  [types.SET_CONVERSATION_CAN_REPLY](_state, { conversationId, canReply }) {
    replaceConversationInList(_state, conversationId, draft => {
      draft.can_reply = canReply;
    });

    const chatCurrent =
      _state.currentConversationId === conversationId
        ? _state.currentConversation
        : undefined;

    if (chatCurrent) TryVueSet(chatCurrent, 'can_reply', canReply);
  },

  [types.CLEAR_CONTACT_CONVERSATIONS](_state, contactId) {
    // ??
    const chats = _state.allConversations.filter(
      c => c.meta.sender.id !== contactId
    );
    _state.allConversations = chats;
    rebuildConversationIndex(_state);
  },

  [types.SET_CONVERSATION_FILTERS](_state, data) {
    _state.appliedFilters = data;
  },

  [types.CLEAR_CONVERSATION_FILTERS](_state) {
    _state.appliedFilters = [];
  },

  [types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION](
    _state,
    { conversationId, messageId }
  ) {
    _state.syncConversationsMessages[conversationId] = messageId;
  },

  [types.SET_CONTEXT_MENU_CHAT_ID](_state, chatId) {
    _state.contextMenuChatId = chatId;
  },

  [types.SET_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = data;
  },

  [types.UPDATE_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = { ..._state.conversationFilters, ...data };
  },

  [types.REMOVE_NON_ASSIGNED_CONVERSATION_SUBSCRIPTION](
    _state,
    conversationId
  ) {
    _state.subscribedNonAssignedConversations =
      _state.subscribedNonAssignedConversations.filter(
        id => id !== conversationId
      );
  },

  [types.HANDLE_SUBSCRIBE_NON_ASSIGNED_CONVERSATION](_state, conversationId) {
    const newList = [..._state.subscribedNonAssignedConversations];

    const existingIndex = newList.findIndex(item => item === conversationId);
    if (existingIndex !== -1) {
      newList.splice(existingIndex, 1);
    }

    newList.push(conversationId);

    if (newList.length > MAX_SUBSCRIBED_CONVERSATIONS) {
      newList.splice(0, newList.length - MAX_SUBSCRIBED_CONVERSATIONS);
    }

    _state.subscribedNonAssignedConversations = newList;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
